import type { BasketItem } from "~~/models/baskets";
import type { InvoiceListItem, OrderListItem } from "~~/models/documents";
import type { CategoryFilter, FilterGroupMultiSelect } from "~~/models/filters";
import type { Pagination } from "~~/models/pagination";
import type { ProductListItem } from "~~/models/products";
import type { TemplateDetailItem } from "~~/models/templates";
import type { PlanktonProductSearchIdResponse } from "~~/server/plankton";

export const defaultCatalogPage = 1;
export const defaultCatalogPageSize = 30;

export function useCatalogPagination(path: string): Ref<Pagination> {
  return useState<Pagination>(
    `catalog-pagination-${path}`,
    () => reactive<Pagination>({
      pageSize: defaultCatalogPageSize,
      pageSizeModifier: 0,
      totalItems: 0,
    }),
  );
}

export function useCatalogDisplay(path: string): Ref<Array<ProductListItem>> {
  return useState<Array<ProductListItem>>(
    `catalog-display-${path}`,
    () => reactive<Array<ProductListItem>>([]),
  );
}

export function useCatalogPaged(path: string): Ref<{
    [key: number]: PlanktonProductSearchIdResponse[];
}> {
  return useState<{
    [key: number]: PlanktonProductSearchIdResponse[];
}>(
    `catalog-paged-${path}`,
    () => reactive<{
        [key: number]: PlanktonProductSearchIdResponse[];
    }>({}),
  );
}

export function useCatalogSearchState(path: string): Ref<any> {
  return useState<any>(
    `catalog-search-state-${path}`,
    () => reactive<{
      term: string | null | undefined;
      lastDays: number | null | undefined;
      categoryFilters: Array<CategoryFilter>;
      propertyFilters: Array<FilterGroupMultiSelect>;
      sortOrder: number | null | undefined;
      sortOrderOptions: Array<number>;
      productIdList: Array<number>;
      page: number;
      pageSize: number;
      isFallback: boolean | null | undefined;
    }>({
      term: undefined,
      lastDays: undefined,
      categoryFilters: [],
      propertyFilters: [],
      sortOrder: undefined,
      sortOrderOptions: [],
      productIdList: [],
      page: defaultCatalogPage,
      pageSize: defaultCatalogPageSize,
      isFallback: undefined,
    }),
  );
}

export function useContentSearchDisplay(key: string): Ref<any> {
  return useState<any>(
    `content-search-display-${key}`,
    () => reactive<any>([]),
  );
}

export function useContentSearchPagination(key: string): Ref<Pagination> {
  return useState<Pagination>(
    `content-search-pagination-${key}`,
    () => reactive<Pagination>({
      pageSize: 0,
      pageSizeModifier: 0,
      totalItems: 0,
    }),
  );
}

export function useContentSearchResults(key: string): Ref<any> {
  return useState<any>(
    `content-search-results-${key}`,
    () => reactive<any>([]),
  );
}

export function useContentSearchSearchState(key: string): Ref<any> {
  return useState<any>(
    `content-search-search-state-${key}`,
    () => reactive<{
      term: string | null | undefined;
      page: number;
    }>({
      term: undefined,
      page: 1,
    }),
  );
}

export function useCheckoutAvailableDates(): Ref<Date[]> {
  return useState<Date[]>(
    "checkout-available-dates",
    () => ref([]),
  );
}

export function useCurrentBasket(key: string): Ref<BasketItem> {
  return useState(
    `current-basket-${key}`,
    () => reactive<BasketItem>({} as BasketItem),
  );
}

export function useDocumentsPagination(): Ref<Pagination> {
  return useState<Pagination>(
    "documents-pagination",
    () => reactive<Pagination>({
      pageSize: 0,
      pageSizeModifier: 0,
      totalItems: 0,
    }),
  );
}

export function useDocumentsDisplay(): Ref<Array<InvoiceListItem | OrderListItem>> {
  return useState<Array<InvoiceListItem | OrderListItem>>(
    "documents-display",
    () => reactive<Array<InvoiceListItem | OrderListItem>>([]),
  );
}

export function useDocumentsPaged(): Ref<any> {
  return useState<any>(
    "documents-paged",
    () => reactive<any>([]),
  );
}

export function useDocumentsSearchState(documentType: string): Ref<any> {
  return useState<any>(
    `documents-search-state-${documentType}`,
    () => reactive<{
      term: string | null | undefined;
      dateFrom: Date | null | undefined;
      dateTo: Date | null | undefined;
      status: number | null | undefined;
      page: number;
    }>({
      term: undefined,
      dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 3)), // minus 3 months
      dateTo: new Date(new Date().setDate(new Date().getDate())), // today
      status: undefined,
      page: 1,
    }),
  );
}

export function useReferrer(): Ref<string | undefined> {
  return useState<string | undefined>(
    "referrer",
    () => ref(undefined),
  );
}

export function useSearchTerm(key: string): Ref<string> {
  return useState<string>(key, () => ref(""));
}

export function useTemplate(key: string): Ref<TemplateDetailItem> {
  return useState(
    `template-${key}`,
    () => reactive<TemplateDetailItem>({} as TemplateDetailItem),
  );
}

/**
 * Global counter tracking how many slidecovers are currently open.
 * Any component can increment/decrement this. ImpersonateBanner hides when > 0.
 */
export function useIsSlideoverOpen(): Ref<number> {
  return useState<number>("is-slideover-open", () => ref(0));
}
